<template>
  <div>
    <div class="animated fadeIn">

      <!-- Pending mapping requests card -->
      <b-card
        v-if="mappingRequests.length > 0"
        border-variant="warning"
        class="mb-3"
        no-body
      >
        <template v-slot:header>
          <div>
            <i class="fa fa-clock-o mr-1"></i>
            Pending Requests
            <span class="badge badge-warning ml-1">{{ mappingRequests.length }}</span>
          </div>
        </template>
        <b-table
          :items="mappingRequests"
          :fields="requestFields"
          head-variant="light"
          head-row-variant="light"
          table-variant="warning"
          bordered
          responsive
          small
          class="mb-0"
        >
          <template v-slot:cell(action)="data">
            <span v-if="data.item.action === 'Create'" class="badge badge-success">Create</span>
            <span v-else-if="data.item.action === 'Remove'" class="badge badge-danger">Remove</span>
            <span v-else-if="data.item.action === 'Update'" class="badge badge-info">Update</span>
            <span v-else class="badge badge-secondary">{{ data.item.action }}</span>
          </template>
          <template v-slot:cell(wmsItem)="data">
            <span v-if="data.item.wmsProduct">{{ data.item.wmsProduct.item }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          <template v-slot:cell(notes)="data">
            <span v-if="data.item.notes">{{ data.item.notes }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          <template v-slot:cell(addedByUserName)="data">
            <span v-if="data.item.addedByUserName">{{ data.item.addedByUserName }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          <template v-slot:cell(requestActions)="data">
            <b-button
              variant="success"
              size="sm"
              class="mr-1"
              :disabled="approvingRequestId === data.item.id || denyingRequestId === data.item.id"
              @click="ApproveRequest(data.item)"
            >
              <i v-if="approvingRequestId === data.item.id" class="fa fa-spinner fa-spin"></i>
              <span v-else>Approve</span>
            </b-button>
            <b-button
              variant="danger"
              size="sm"
              :disabled="approvingRequestId === data.item.id || denyingRequestId === data.item.id"
              @click="DenyRequest(data.item)"
            >
              <i v-if="denyingRequestId === data.item.id" class="fa fa-spinner fa-spin"></i>
              <span v-else>Deny</span>
            </b-button>
          </template>
        </b-table>
      </b-card>

      <!-- Existing mappings card -->
      <b-card no-body>
        <template v-slot:header>
          <div>
            <i class="fa fa-link mr-1"></i>
            Current Mappings
          </div>
        </template>

        <b-alert
          v-if="isMappingProgress"
          show
          variant="warning"
          class="text-center m-1"
        >
          <h4 class="alert-heading text-center">
            Submitting request...
          </h4>
          <p class="text-center mb-0">
            Please wait...
          </p>
        </b-alert>
        <b-alert
          v-else-if="!isContains && productsMappedList.length>0"
          show
          variant="warning"
          class="text-center m-1"
        >
          <h4 class="alert-heading text-center">
            Cannot map more items!
          </h4>
          <p class="text-center mb-0">
            This is a <b>Single Item</b> Storefront Product. You cannot map more items to it. Remove the already mapped items to add new ones, or change it to a bundle if it needs to fulfill as multiple items instead.
          </p>
        </b-alert>
        <b-row v-else class="p-3 pb-0">
          <div
            class="col-5"
            :class="{ 'col-sm-6': isContains, 'col-sm-9': !isContains }"
          >
            <b-form-group label="Warehouse Item">
              <v-select
                v-model="toMapWmsShipProductsArr"
                multiple
                :options="productListWMSPos"
              />
            </b-form-group>
          </div>
          <div
            v-if="isContains"
            class="col-3 text-center align-items-center justify-content-center"
          >
            <b-form-group label="Quantity">
              <input v-model="quantMap" type="text" class="form-control" />
            </b-form-group>
          </div>
          <div class="col-2 col-sm-2 text-center align-items-center justify-content-center">
            <b-form-group label=".">
              <button class="btn btn-block btn-primary" @click="MapProduct">
                Request Map
              </button>
            </b-form-group>
          </div>
        </b-row>

        <TableListing
          :items="productsMappedList"
          :fields="isContains ? fields: fieldsSynonym"
          :options.sync="tableOptions"
          :show-pagination-row="false"
          :pagination.sync="pagination"
        >
          <template v-slot:cell(projectId)="data">
            {{ GetProjectName(data.item.projectId) }}
          </template>
          <template v-slot:cell(quantityAvail)="data">
            <span>{{
              data.item.type == 0
                ? '.. Same As ..'
                : 'Contains ' + data.item.quantityAvail + ' Of'
            }}</span>
          </template>
          <template v-slot:cell(shipDate)="data">
            <span v-if="data.item.shipDate">{{
              data.item.shipDate | moment('MMM Do YY, h:mm a')
            }}</span>
          </template>
          <template v-slot:cell(defaultSalesPrice)="data">
            <div class="d-flex align-items-center">
              <span>{{ data.item.defaultSalesPrice != null ? '$' + Number(data.item.defaultSalesPrice).toFixed(2) : '- Not Set -' }}</span>
              <b-button
                :id="'price-edit-' + data.item.id"
                variant="link"
                size="sm"
                class="ml-1 p-0"
              >
                <i class="fa fa-pencil"></i>
              </b-button>
              <b-popover
                container="body"
                boundary="viewport"
                :target="'price-edit-' + data.item.id"
                triggers="click"
                placement="left"
                @show="onPriceEditShow(data.item)"
              >
                <div class="d-flex align-items-center">
                  <b-form-input
                    v-model="editingPrice"
                    type="number"
                    step="0.01"
                    size="sm"
                    style="width: 100px"
                    class="mr-2"
                  />
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="savePrice(data.item)"
                  >
                    Save
                  </b-button>
                </div>
              </b-popover>
            </div>
          </template>
          <template v-slot:cell(addedByUserName)="data">
            <span v-if="data.item.addedByUserName">{{ data.item.addedByUserName }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          <template v-slot:cell(reviewedByUserName)="data">
            <span v-if="data.item.reviewedByUserName">{{ data.item.reviewedByUserName }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          <template v-slot:cell(wmsProduct.id)="data">
            <button class="btn btn-danger" @click="RequestDelete(data.item.id)">
              Request Remove
            </button>
          </template>
        </TableListing>
      </b-card>
<!----
      <div class="mt-3">
        <div>
          <div class="mt-2">
            <h3 class="d-inline-block mr-3">
              Recommended Items to map to:
            </h3>
            <b-row>
              <div class="col-4">
                <b-form-group label="Search by">
                  <b-form-select v-model="automapFilters.similarBy">
                    <option value="sku">
                      SKU
                    </option>
                    <option value="descriptions">
                      Descriptions
                    </option>
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-4">
                <b-form-group label="Similarity">
                  <b-form-select v-model="automapFilters.similarHow">
                    <option value="same">
                      Same
                    </option>
                    <option value="similar">
                      Similar
                    </option>
                    <option value="somewhat-similar">
                      Somewhat Similar
                    </option>
                  </b-form-select>
                </b-form-group>
              </div>
            </b-row>
            <div>
              <b-card class="nopadinr">
                <b-table
                  head-variant="dark"
                  class="m-0"
                  :responsive="true"
                  :bordered="true"
                  :items="productMappingRecommended"
                  :fields="fieldsRec"
                  :per-page="100"
                >
                  <template v-slot:cell(id)="data">
                    <b-button
                      variant="primary"
                      @click="MapRecommended(data.item.id, data.item.item)"
                    >
                      MAP
                    </b-button>
                  </template>
                </b-table>
                <div v-if="loadingPageRec" class="alert alert-info text-center">
                  <div class="text-center mb-3" style="font-size: 40px">
                    <i class="fa fa-spinner fa-spin" />
                  </div>
                  <h3>Loading...</h3>
                </div>
                <div
                  v-if="
                    !loadingPageRec &&
                      productMappingRecommended &&
                      productMappingRecommended.length <= 0
                  "
                  class="alert alert-warning text-center"
                >
                  <h3>No Recommendations To Show</h3>
                </div>
              </b-card>
            </div>
          </div>
        </div>
      </div>
--->

    </div>
  </div>
</template>

<script>
const _debaunce = function(func, wait, immediate) {
  let timeout;
  return function() {
    let context = this,
      args = arguments;
    let later = function() {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    let callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};
import TableListing from '@/views/components/TableListing.vue';
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
export default {
  name: 'Products',
  mixins: [myCommonDataMixin],
  components: {
    TableListing
  },
  props: {
    projectId: {
      type: String,
      required: true
    },
    productId: {
      type: String,
      required: true
    },
    isContains: {
      type: Boolean,
      default: true,
      required: true
    }
  },
  data() {
    return {
      productMappingRecommended: [],
      loadingPageRec: false,
      automapFilters: {
        similarBy: 'sku',
        similarHow: 'similar'
      },
      selectedSkuItem: null,
      itemSkuRecs: [],
      itemSkuRecsFull: [],
      itemForm: {
        id: null,
        status: 'active',
        type: 'Stock Item',
        wholesalePrice: 0,
        factoringPercentage: 0,
        description: '',
        inventoryStatus: 0,
        item: ''
      },
      isMappingProgress: false,
      productsMappedListAll: [],
      toMapShipProducts: '',
      toMapWmsShipProducts: '',
      quantMap: 1,
      toMapWmsShipProductsArr: [],
      loadingPage: false,
      pagination: {
        perPage: 10,
        page: 1,
        loadedAll: true
      },
      tableOptions: {
        showAll: true,
        totalRows: 0,
        loadedAll: true
      },
      productMappingFilters: {
        type: 0,
        productIdFilter: '',
        sortBy: 'OrderDate',
        sortDir: 'DESC',
        customerName: '',
        storeFront: '',
        orderStatus: '',
        customerUsername: '',
        perPage: 10
      },
      itemChange: {
        address: '',
        city: '',
        state: '',
        postcode: '',
        carrierCode: '',
        serviceCode: '',
        serviceFullName: '',
        orderItems: [],
        clientId: '',
        coupon: '',
        projectId: ''
      },
      searchtxt2: '',
      showModalChangeNewItem: false,
      wmsProductsListAll: [],
      projectAll: [],
      fieldsRec: [
        { key: 'item', label: 'Warehouse Item', sortable: false },
        { key: 'description', label: 'Description', sortable: false },
        { key: 'quantity', label: 'On Hand', sortable: false },
        { key: 'id', label: 'Action', sortable: false }
      ],
      editingPrice: null,
      mappingRequests: [],
      approvingRequestId: null,
      denyingRequestId: null,
      requestFields: [
        { key: 'action', label: 'Action', sortable: false },
        { key: 'wmsItem', label: 'WMS Item', sortable: false },
        { key: 'quantity', label: 'Qty', sortable: false },
        { key: 'addedByUserName', label: 'Requested By', sortable: false },
        //{ key: 'notes', label: 'Notes', sortable: false },
        { key: 'requestActions', label: 'Actions', sortable: false }
      ]
    };
  },
  computed: {
    // sortByNum: {
    //   get() {
    //     this.sortByNumInt;
    //   },
    //   set(val, old) {
    //     if(val != null) {
    //       this.sortByNumInt = val;
    //       if(val == 2) {
    //         this.productMappingFilters.sortBy = 'OrderDate'
    //         this.productMappingFilters.sortDesc = 'DESC'
    //       } else  if(val == 3) {
    //         this.productMappingFilters.sortBy = 'OrderDate'
    //         this.productMappingFilters.sortDesc = 'ASC'
    //       } else  if(val == 1) {
    //         this.productMappingFilters.sortBy = 'OrderId'
    //         this.productMappingFilters.sortDesc = 'ASC'
    //       } else  if(val == 0) {
    //         this.productMappingFilters.sortBy = 'OrderId'
    //         this.productMappingFilters.sortDesc = 'ASC'
    //       }
    //       this.loadMappings();
    //     }
    //   }
    // },
    productsMappedList: {
      get() {
        let list = this.productsMappedListAll;
        return list;
      },
      set(val, old) {
        this.productsMappedListAll = val;
      }
    },
    productListWMSPos: {
      get() {
        let arrs = [];
        this.wmsProductsListAll.forEach(itm => {
          if (!this.searchtxt2) {
            arrs.push({
              code: itm.id,
              label: itm.item + ' (' + itm.description + ')'
            });
          } else if (
            itm.item.toLowerCase().includes(this.searchtxt2.toLowerCase()) ||
            itm.description
              .toLowerCase()
              .includes(this.searchtxt2.toLowerCase())
          ) {
            arrs.push({
              code: itm.id,
              label:
                itm.item +
                ' (' +
                itm.description +
                '; QTY: ' +
                itm.quantity +
                ')'
            });
          }
        });
        return arrs;
      },
      set(old, val) {}
    },
    projectActive: {
      get() {
        let proj = null;
        //  arrs.push({ value: '', text: 'All' })
        proj = this.projectAll.find(a => a.id == this.projectId);
        return proj;
      },
      set(old, val) {}
    },
    useCustomPricing() {
      return this.isContains && this.projectActive && this.projectActive.useCustomPricingOnBundleMappings;
    },
    fields() {
      var f = [
        { key: 'quantity', label: 'Qty', sortable: false },
        { key: 'wmsProduct.item', label: 'Warehouse Item', sortable: false },
        { key: 'wmsProduct.description', label: 'Description', sortable: false },
        { key: 'wmsProduct.quantityAvail', label: 'Available', sortable: false }
      ];
      if (this.useCustomPricing) {
        f.push({ key: 'defaultSalesPrice', label: 'Default Sales Price', sortable: false });
      }
      f.push({ key: 'addedByUserName', label: 'Mapped By', sortable: false });
      f.push({ key: 'reviewedByUserName', label: 'Approved By', sortable: false });
      f.push({ key: 'wmsProduct.id', label: 'Action', sortable: false });
      return f;
    },
    fieldsSynonym() {
      var f = [
        { key: 'wmsProduct.item', label: 'Warehouse Item', sortable: false },
        { key: 'wmsProduct.description', label: 'Description', sortable: false },
        { key: 'wmsProduct.quantityAvail', label: 'Available', sortable: false }
      ];
      if (this.useCustomPricing) {
        f.push({ key: 'defaultSalesPrice', label: 'Sales Price', sortable: false });
      }
      f.push({ key: 'addedByUserName', label: 'Mapped By', sortable: false });
      f.push({ key: 'reviewedByUserName', label: 'Approved By', sortable: false });
      f.push({ key: 'wmsProduct.id', label: 'Action', sortable: false });
      return f;
    }
  },
  watch: {
    automapFilters: {
      handler: function(val, oldVal) {
        this.loadRecs();
      },
      deep: true
    },
    selectedSkuItem: {
      handler: function(val, oldVal) {
        let src = val;
        setTimeout(() => {
          let fnd = this.itemSkuRecsFull.find(
            a => val == a.sku + ' - ' + a.description
          );
        }, 400);
      }
    },
    'itemForm.item': {
      handler: function(val, oldval) {
        let $this = this;
        _debaunce(function(val) {
          $this.getERPRecs(val);
        }, 300)();
      }
    },
    projectId: {
      handler: function(val, oldVal) {
        this.itemChange.projectId = val;
        this.loadMappingsInit();
      }
    }
  },
  mounted() {
    this.itemChange.projectId = this.projectId;
    this.loadMappingsInit();
  },
  methods: {
    loadRecs() {
      this.productMappingRecommended = [];
      this.$http
        .post('/api/warehouseProducts/recs', {
          productId: this.productId,
          warehouseId: this.projectActive.warehouseId,
          similarBy: this.automapFilters.similarBy,
          similarHow: this.automapFilters.similarHow
        })
        .then(resp => {
          if (resp.data.items) {
            this.productMappingRecommended = resp.data.items;
          }
        })
        .catch(err => {});
    },
    getERPRecs(query) {
      if (this.projectActive) {
        this.$http
          .post('/api/erpProducts/recs', {
            productId: this.productId,
            warehouseId: this.projectActive.warehouseId,
            query: query
          })
          .then(resp => {
            if (resp.data.success) {
              this.itemSkuRecs = [];
              this.itemSkuRecsFull = resp.data.items;
              resp.data.items.forEach(it => {
                this.itemSkuRecs.push(it.sku + ' - ' + it.description);
              });
            }
          })
          .catch(err => {
            console.error(' erpProductserr', err);
          });
      }
    },
    AddNewWareProduct() {
      if (this.projectActive) {
        this.OnWareReset();
        this.itemForm.warehouseId = this.projectActive.warehouseId;
        this.itemForm.brandId = this.projectActive.brandId;
        this.showModalChangeNewItem = true;
      }
    },
    MapProduct() {
      if (
        this.productId &&
        (this.toMapWmsShipProducts || this.toMapWmsShipProductsArr.length > 0)
      ) {
        // let prod = this.toMapShipProducts;
        this.isMappingProgress = true;
        let wmsProd = this.toMapWmsShipProducts;
        let wmsProds = [];
        if (this.toMapWmsShipProducts) {
          wmsProds.push(this.toMapWmsShipProducts);
        } else {
          this.toMapWmsShipProductsArr.forEach(it => {
            wmsProds.push(it.code);
          });
        }
        let mapType = 1;
        if (this.isContains) {
          mapType = 1;
        } else {
          mapType = 0;
        }
        this.$http
          .post('/api/common/productMappingRequests/', {
            action: 0,
            prodId: this.productId,
            wmsIds: wmsProds,
            type: mapType,
            quantity: this.quantMap
          })
          .then(resp => {
            if (resp.data.success) {
              this.toMapShipProducts = '';
              this.toMapWmsShipProducts = '';
              this.toMapWmsShipProductsArr = [];
              this.quantMap = 1;
              this.isMappingProgress = false;
              this.$swal('Mapping request submitted for approval.');
              this.loadMappings();
              this.loadMappingRequests();
            } else {
              this.isMappingProgress = false;
              alert('Request could not be submitted.');
            }
          })
          .catch(err => {
            if (err.response && err.response.data.errorMessage) {
              this.$swal({ title: err.response.data.errorMessage, icon: 'error' });
            } else {
              this.$swal({ title: 'Something went wrong!', icon: 'error' });
            }
            console.error('productMappingRequests err', err);
            this.isMappingProgress = false;
          });
      }
    },
    GetProjectName(projId) {
      let projName = '';
      let fnd = this.myProjects.find(s => s.id == projId);
      if (fnd) {
        projName = fnd.name;
      }
      return projName;
    },
    loadMappingsInit(hnl = () => {}) {
      this.loadMappings();
      this.loadMappingRequests();
      this.$http
        .post('/api/warehouseProducts/byProject/' + this.projectId)
        .then(resp => {
          this.wmsProductsListAll = resp.data;
          hnl();
        })
        .catch(err => {
          console.error('warehouseProducts/byProject err', err);
        });
    },
    loadMappingRequests() {
      this.$http
        .post('/api/common/productMappingRequests/list/by-product', {
          productId: this.productId,
          showAll: true
        })
        .then(resp => {
          if (resp.data && resp.data.items) {
            this.mappingRequests = resp.data.items;
          }
        })
        .catch(err => {
          console.error('productMappingRequests/list/by-product err', err);
        });
    },
    loadMappings() {
      this.productMappingFilters.perPage = parseInt(this.pagination.perPage);
      if (this.isContains) {
        this.productMappingFilters.type = 1;
      } else {
        this.productMappingFilters.type = 0;
      }
      var body = Object.assign({}, this.productMappingFilters, {
        productId: this.productId || this.productMappingFilters.productIdFilter
      });
      this.$http
        .post('/api/common/productMappings/list/by-product', body)
        .then(resp => {
          this.loadingPage = false;
          let prods = resp.data.items;

          this.productsMappedListAll = prods;
          this.isMappingProgress = false;
          this.tableOptions.totalRows = resp.data.total;
        })
        .catch(err => {
          console.error('productMappings list/by-product err', err);
          this.isMappingProgress = false;
        });
    },

    OnWareReset() {
      this.itemForm = {
        id: null,
        type: 'Stock Item',
        wholesalePrice: 0,
        factoringPercentage: 0,
        description: '',
        inventoryStatus: 0,
        item: ''
      };
    },
    SaveWareItem() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          return;
        }
        this.itemForm.wholesalePrice = parseFloat(this.itemForm.wholesalePrice);
        this.itemForm.factoringPercentage = parseFloat(
          this.itemForm.factoringPercentage
        );
        if (
          this.itemForm.factoringPercentage > 100 ||
          this.itemForm.factoringPercentage < 0
        ) {
          this.itemForm.factoringPercentage = 0;
        }
        this.$http
          .post(
            '/api/warehouseProducts' + (this.itemForm.id ? '/edit' : ''),
            this.itemForm
          )
          .then(resp => {
            this.showModalChangeNewItem = false;
            if (resp.data.id) {
              this.$swal({ title: 'Item Saved!', icon: 'success' });
              // this.$router.push({name: 'Warehouses.Index'})
              this.loadMappingsInit(() => {
                this.toMapWmsShipProductsArr.push({
                  code: resp.data.id,
                  label:
                    resp.data.item +
                    ' (' +
                    resp.data.description +
                    '; QTY: ' +
                    resp.data.quantity +
                    ')'
                });
              });
            } else {
              this.$swal({ title: 'Item Was NOT Saved!', icon: 'error' });
            }
          })
          .catch(err => {
            this.$swal({ title: 'Item Was NOT Saved!', icon: 'error' });
            console.error('saved po err', err);
          });
      });
    },

    onPriceEditShow(item) {
      this.editingPrice = item.defaultSalesPrice != null ? item.defaultSalesPrice : '';
    },
    savePrice(item) {
      var price = this.editingPrice !== '' && this.editingPrice != null ? parseFloat(this.editingPrice) : null;
      this.$http
        .post('/api/common/productMappings/' + item.id + '/updatePrice', { defaultSalesPrice: price })
        .then(function (resp) {
          item.defaultSalesPrice = price;
          this.$root.$emit('bv::hide::popover');
          this.$swal({ title: 'Price Updated!', icon: 'success' });
        }.bind(this))
        .catch(function (err) {
          if (err.response && err.response.data && err.response.data.errorMessage) {
            this.$swal({ title: err.response.data.errorMessage, icon: 'error' });
          } else {
            this.$swal({ title: 'Failed to update price!', icon: 'error' });
          }
        }.bind(this));
    },
    RequestDelete(id) {
      var $this = this;
      $this.$swal({
        title: 'Request removal?',
        text: 'This will submit a request to remove this mapping. It will be removed once approved.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, submit request'
      }).then(function(result) {
        if (result.value) {
          $this.$http
            .post('/api/common/productMappingRequests/', {
              action: 1,
              parentProductMappingId: id
            })
            .then(function(resp) {
              if (resp.data && resp.data.success) {
                $this.$swal('Submitted', 'Removal request submitted for approval.', 'success');
                $this.loadMappings();
                $this.loadMappingRequests();
              } else {
                $this.$swal('Error', (resp.data && resp.data.errorMessage) || 'Could not submit request.', 'error');
              }
            })
            .catch(function(err) {
              var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'Something went wrong.';
              $this.$swal('Error', msg, 'error');
            });
        }
      });
    },

    ApproveRequest: function(item) {
      var $this = this;
      $this.approvingRequestId = item.id;
      $this.$http
        .post('/api/common/productMappingRequests/' + item.id + '/approve')
        .then(function(resp) {
          if (resp.data && resp.data.success) {
            $this.$swal('Approved', 'Mapping request approved.', 'success');
            $this.loadMappings();
            $this.loadMappingRequests();
          } else {
            $this.$swal('Error', (resp.data && resp.data.errorMessage) || 'Failed to approve request.', 'error');
          }
        })
        .catch(function(err) {
          var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'Failed to approve request.';
          $this.$swal('Error', msg, 'error');
        })
        .finally(function() {
          $this.approvingRequestId = null;
        });
    },

    DenyRequest: function(item) {
      var $this = this;
      $this.$swal({
        title: 'Deny this request?',
        text: 'This will permanently delete the mapping request.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, deny it'
      }).then(function(result) {
        if (result.value) {
          $this.denyingRequestId = item.id;
          $this.$http
            .delete('/api/common/productMappingRequests/' + item.id)
            .then(function(resp) {
              $this.$swal('Denied', 'Mapping request denied.', 'success');
              $this.loadMappings();
              $this.loadMappingRequests();
            })
            .catch(function(err) {
              var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'Failed to deny request.';
              $this.$swal('Error', msg, 'error');
            })
            .finally(function() {
              $this.denyingRequestId = null;
            });
        }
      });
    }
  }
};
</script>

